import { SettingOutlined } from '@ant-design/icons'
import { useMemo, useRef, type CSSProperties, type KeyboardEvent, type MouseEvent, type RefObject } from 'react'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { IPatientConsultationStaff } from 'features/Home/interfaces/IInfoPatient'
import { useFetchEmrLinkedProvidersQuery } from 'features/Patients/state/api/patientApi'
import { getEmrLinkedResidenUserIds } from 'features/Patients/state/api/emrProviders.transform'
import {
  getScheduleStaffColorByIndex,
  getScheduleStaffInitials,
  getScheduleStaffShortName,
} from './scheduleInstance'
import styles from './scheduleDoctorRail.module.scss'

const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
})

type ScheduleDoctorRailProps = {
  staff: IPatientConsultationStaff[]
  focusedStaffIds: number[]
  onToggleStaff: (staffId: number) => void
  onClear: () => void
  onReorder: (orderedIds: number[]) => void
  onEditStaff: (staff: IPatientConsultationStaff) => void
}

type DoctorRailTabProps = {
  color: string
  member: IPatientConsultationStaff
  active: boolean
  muted: boolean
  suppressClickRef: RefObject<boolean>
  canEdit: boolean
  onToggleStaff: (staffId: number) => void
  onEditStaff: (staff: IPatientConsultationStaff) => void
}

const DoctorRailTab = ({
  color,
  member,
  active,
  muted,
  suppressClickRef,
  canEdit,
  onToggleStaff,
  onEditStaff,
}: DoctorRailTabProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: member.id,
  })

  const style: CSSProperties = {
    borderColor: active ? color : undefined,
    opacity: isDragging ? 1 : muted ? 0.4 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onToggleStaff(member.id)
  }

  const handleEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onEditStaff(member)
  }

  return (
    <div
      ref={setNodeRef}
      role='button'
      tabIndex={0}
      className={[styles.tab, isDragging && styles.dragging, active && styles.active]
        .filter(Boolean)
        .join(' ')}
      style={style}
      aria-pressed={active}
      {...attributes}
      {...listeners}
      onClick={() => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false
          return
        }
        onToggleStaff(member.id)
      }}
      onKeyDown={handleKeyDown}>
      <span className={styles.bar} style={{ background: color }} />
      {canEdit ? (
        <button
          type='button'
          className={styles.gear}
          aria-label={`Edit schedule for ${member.name}`}
          title='Edit schedule'
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={handleEdit}>
          <SettingOutlined />
        </button>
      ) : null}
      <span className={styles.name}>{getScheduleStaffShortName(member.name)}</span>
      <span className={styles.initials} style={{ background: color }}>
        {getScheduleStaffInitials(member.name)}
      </span>
    </div>
  )
}

export const ScheduleDoctorRail = ({
  staff,
  focusedStaffIds,
  onToggleStaff,
  onClear,
  onReorder,
  onEditStaff,
}: ScheduleDoctorRailProps) => {
  const hasFocus = focusedStaffIds.length > 0
  const focused = new Set(focusedStaffIds)
  const { data: emrProviders, isSuccess: emrProvidersLoaded } = useFetchEmrLinkedProvidersQuery()
  const emrLinkedUserIds = useMemo(
    () => getEmrLinkedResidenUserIds(emrProviders ?? []),
    [emrProviders],
  )
  const suppressClickRef = useRef(false)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: { y: 6 } } }),
  )

  const handleDragStart = () => {
    suppressClickRef.current = true
  }

  const releaseClickSuppression = () => {
    window.setTimeout(() => {
      suppressClickRef.current = false
    }, 0)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    releaseClickSuppression()
    const { active, over } = event
    if (!over || active.id === over.id) return

    const ids = staff.map((member) => member.id)
    const oldIndex = ids.indexOf(Number(active.id))
    const newIndex = ids.indexOf(Number(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    onReorder(arrayMove(ids, oldIndex, newIndex))
  }

  const handleDragCancel = () => {
    releaseClickSuppression()
  }

  return (
    <aside className={styles.rail} aria-label='Filter schedule by doctor'>
      <div className={styles.head}>Dr</div>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}>
        <SortableContext
          items={staff.map((member) => member.id)}
          strategy={verticalListSortingStrategy}>
          <div className={styles.list}>
            {staff.map((member, index) => (
              <DoctorRailTab
                key={member.id}
                member={member}
                color={getScheduleStaffColorByIndex(index)}
                active={focused.has(member.id)}
                muted={hasFocus && !focused.has(member.id)}
                suppressClickRef={suppressClickRef}
                canEdit={emrProvidersLoaded && emrLinkedUserIds.has(member.id)}
                onToggleStaff={onToggleStaff}
                onEditStaff={onEditStaff}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className={styles.footer}>
        <button
          type='button'
          className={hasFocus ? `${styles.clear} ${styles.clearActive}` : styles.clear}
          onClick={onClear}
          disabled={!hasFocus}
          title='Clear provider filter'
          aria-label='Clear provider filter'>
          ✕
        </button>
      </div>
    </aside>
  )
}
